import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../AuthContext";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import BookingDetailDialog from "../components/BookingDetailDialog";
import { loadPublicDomains, guestBase } from "../publicDomains";
import {
  Sun, Sparkles, ChevronRight, Loader2, CalendarArrowDown, CalendarArrowUp, LogIn,
  FileCheck2, DoorOpen, Wrench, AlertTriangle, CheckCircle2, Home, Globe,
  ClipboardList, Send, ExternalLink, Clock, MapPin, Eye,
} from "lucide-react";

const COLOR = {
  green: { dot: "#059669", ring: "ring-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700" },
  blue: { dot: "#2563EB", ring: "ring-blue-200", bg: "bg-blue-50", text: "text-blue-700" },
  orange: { dot: "#EA580C", ring: "ring-orange-200", bg: "bg-orange-50", text: "text-orange-700" },
  yellow: { dot: "#CA8A04", ring: "ring-amber-200", bg: "bg-amber-50", text: "text-amber-700" },
  red: { dot: "#DC2626", ring: "ring-rose-200", bg: "bg-rose-50", text: "text-rose-700" },
};
const ICONS = {
  arrivi: CalendarArrowDown, partenze: CalendarArrowUp, checkin: LogIn, documenti: FileCheck2,
  accessi: DoorOpen, pulizie_oggi: Sparkles, pulizie_ritardo: AlertTriangle,
  manutenzioni: Wrench, manutenzioni_urgenti: AlertTriangle,
};

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Buongiorno" : h < 18 ? "Buon pomeriggio" : "Buonasera";
}

const LANG_FLAG = { it: "🇮🇹", en: "🇬🇧", es: "🇪🇸", fr: "🇫🇷", de: "🇩🇪", pt: "🇵🇹", nl: "🇳🇱" };

const PORTAL_STATUS_LABEL = {
  never_opened: "Mai aperto", opened: "Aperto", checkin_started: "Check-in iniziato", completed: "Check-in completato",
};
const URGENCY = {
  urgente: { label: "Urgente", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  attenzione: { label: "Attenzione", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  normale: { label: "Normale", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const GROUP_LABEL = { today: "Arrivo oggi", "24h": "Arrivo entro 24 ore", "48h": "Arrivo entro 48 ore", later: "Arrivi successivi" };
const BUCKET_COLOR = {
  never_opened: "text-slate-600", checkin_incomplete: "text-orange-600",
  tax_due: "text-indigo-600", integration_requested: "text-rose-600",
};

function GuestPortalsCard() {
  const [d, setD] = useState(undefined); // undefined=loading, null=hidden
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [busy, setBusy] = useState("");
  const [domains, setDomains] = useState(null);

  const load = () => api.get("/today/guest-portals").then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); loadPublicDomains().then(setDomains); }, []);

  if (d === undefined) return null;
  if (d === null) return null;

  const remind = async (item) => {
    setBusy(item.booking_id);
    const link = `${guestBase(domains)}${item.portal_path}`;
    const first = (item.guest_name || "").split(" ")[0];
    const msg = `Ciao ${first}, per completare il check-in del tuo soggiorno a ${item.apartment_name} (arrivo ${item.check_in}) accedi al tuo Guest Portal Urban Dreams Home: ${link} — ti bastano pochi minuti. A presto!`;
    try {
      await navigator.clipboard.writeText(`${msg}`);
      await api.post(`/bookings/${item.booking_id}/portal-reminder`);
      toast.success("Messaggio + link copiati · sollecito registrato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(""); }
  };

  const copyLink = async (item) => {
    await navigator.clipboard.writeText(`${guestBase(domains)}${item.portal_path}`);
    toast.success("Link Portale copiato");
  };

  const grouped = ["today", "24h", "48h", "later"].map((g) => ({ g, items: d.items.filter((i) => i.group === g) })).filter((x) => x.items.length);

  return (
    <>
      <Card className="overflow-hidden border-[#EAE7DF]" data-testid="today-guest-portals-card">
        <button onClick={() => d.total > 0 && setOpen(true)} data-testid="guest-portals-open-btn"
          className={`flex w-full items-center gap-2 border-b border-[#EAE7DF] bg-[#F9F8F4] px-5 py-3 text-left ${d.total > 0 ? "hover:bg-[#F3F1E9]" : ""}`}>
          <ClipboardList size={17} className="text-[#C5A059]" />
          <span className="font-head text-sm font-semibold text-slate-800">Portali ospite da completare</span>
          <span className="ml-auto text-3xl font-light tabular-nums text-slate-900" data-testid="guest-portals-total">{d.total}</span>
          {d.total > 0 && <ChevronRight size={18} className="text-slate-300" />}
        </button>
        <div className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-4">
          {Object.keys(d.buckets).map((k) => (
            <button key={k} data-testid={`guest-portals-bucket-${k}`} onClick={() => d.buckets[k] > 0 && setOpen(true)}
              className="rounded-xl border border-[#EAE7DF] bg-white p-3 text-left transition-colors hover:border-[#C5A059]/50">
              <div className={`text-2xl font-light tabular-nums ${BUCKET_COLOR[k]}`}>{d.buckets[k]}</div>
              <div className="mt-0.5 text-xs text-slate-500">{d.bucket_labels[k]}</div>
            </button>
          ))}
        </div>
        {d.total === 0 && <p className="px-5 pb-4 text-sm text-slate-400">Nessun portale ospite in attesa di azione. Ottimo lavoro! 🎯</p>}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="guest-portals-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardList size={18} className="text-[#C5A059]" /> Portali ospite da completare ({d.total})</DialogTitle>
            <DialogDescription>Pratiche che richiedono un'azione dell'ospite prima del check-in, ordinate per urgenza.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {grouped.map(({ g, items }) => (
              <div key={g}>
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <Clock size={12} /> {GROUP_LABEL[g]} · {items.length}
                </div>
                <div className="space-y-2">
                  {items.map((i) => {
                    const u = URGENCY[i.urgency] || URGENCY.normale;
                    return (
                      <div key={i.booking_id} data-testid={`guest-portal-item-${i.booking_id}`} className="rounded-xl border border-slate-200 p-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-slate-800">{i.guest_name || "—"}</span>
                          <span className="flex items-center gap-1 text-xs text-slate-500"><MapPin size={12} /> {i.apartment_name}</span>
                          <Badge variant="outline" className={`ml-auto border ${u.cls}`}>{u.label}</Badge>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500 sm:grid-cols-4">
                          <div><span className="text-slate-400">Arrivo:</span> {i.check_in}{i.checkin_time ? ` ${i.checkin_time}` : ""}</div>
                          <div><span className="text-slate-400">Portale:</span> {PORTAL_STATUS_LABEL[i.portal_status] || i.portal_status}</div>
                          <div><span className="text-slate-400">Ultimo accesso:</span> {i.last_access ? new Date(i.last_access).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</div>
                          <div className={`font-medium ${BUCKET_COLOR[i.bucket]}`}>{i.missing_activity}</div>
                        </div>
                        <div className="mt-2.5 flex flex-wrap items-center gap-2">
                          <Button size="sm" data-testid={`guest-portal-open-${i.booking_id}`} onClick={() => setDetailId(i.booking_id)} className="h-8 gap-1.5 bg-primary px-3 text-xs hover:bg-primary/90">
                            <Eye size={13} /> Apri pratica
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`guest-portal-remind-${i.booking_id}`} disabled={busy === i.booking_id} onClick={() => remind(i)} className="h-8 gap-1.5 px-3 text-xs">
                            {busy === i.booking_id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Sollecita ospite
                          </Button>
                          <button data-testid={`guest-portal-copylink-${i.booking_id}`} onClick={() => copyLink(i)} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                            <ExternalLink size={12} /> Copia link
                          </button>
                          {i.reminder_count > 0 && <span className="text-xs text-slate-400">Sollecitato {i.reminder_count}×</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {d.total === 0 && <p className="py-6 text-center text-sm text-slate-400">Nessuna pratica in attesa.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {detailId && (
        <BookingDetailDialog open={!!detailId} onOpenChange={(o) => { if (!o) { setDetailId(null); load(); } }} bookingId={detailId} />
      )}
    </>
  );
}

function RebookingCard() {
  const [rb, setRb] = useState(undefined); // undefined=loading, null=hidden
  useEffect(() => { api.get("/system/discover-clicks").then((r) => setRb(r.data)).catch(() => setRb(null)); }, []);
  if (rb === undefined || rb === null) return null;
  const maxBar = Math.max(1, ...rb.series_7.map((d) => d.count));
  const stats = [
    { label: "Click totali", value: rb.total }, { label: "Oggi", value: rb.today },
    { label: "Ultimi 7 giorni", value: rb.last_7 }, { label: "Ultimi 30 giorni", value: rb.last_30 },
  ];
  return (
    <Card className="overflow-hidden border-[#EAE7DF]" data-testid="today-rebooking-card">
      <div className="flex items-center gap-2 border-b border-[#EAE7DF] bg-[#F9F8F4] px-5 py-3">
        <Home size={17} className="text-[#C5A059]" />
        <span className="font-head text-sm font-semibold text-slate-800">Interesse rebooking</span>
        <span className="ml-auto text-xs text-slate-400">Ospiti che esplorano gli appartamenti</span>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl border border-[#EAE7DF] bg-white p-3" data-testid={`rebooking-stat-${s.label}`}>
              <div className="text-3xl font-light tabular-nums text-slate-900">{s.value}</div>
              <div className="mt-0.5 text-xs text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Andamento 7 giorni</div>
            <div className="flex h-24 items-end gap-1.5" data-testid="rebooking-trend">
              {rb.series_7.map((d) => (
                <div key={d.date} className="flex flex-1 flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                  <div className="flex w-full items-end justify-center" style={{ height: "72px" }}>
                    <div className="w-full rounded-t bg-[#0A1629]/85" style={{ height: `${(d.count / maxBar) * 100}%`, minHeight: d.count ? "4px" : "0" }} />
                  </div>
                  <span className="text-[9px] text-slate-400">{d.date.slice(8)}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Per appartamento</div>
            {rb.by_apartment.length === 0 ? <p className="text-xs text-slate-400">Nessun click ancora.</p> : (
              <div className="space-y-1.5">
                {rb.by_apartment.map((a) => (
                  <div key={a.apartment} className="flex items-center justify-between text-sm">
                    <span className="min-w-0 flex-1 truncate text-slate-700">{a.apartment}</span>
                    <span className="ml-2 tabular-nums font-medium text-slate-900">{a.clicks}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="mb-2 flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-slate-400"><Globe size={12} /> Per lingua</div>
            {rb.by_language.length === 0 ? <p className="text-xs text-slate-400">—</p> : (
              <div className="space-y-1.5">
                {rb.by_language.map((l) => (
                  <div key={l.lang} className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{LANG_FLAG[l.lang] || "🏳️"} {l.lang.toUpperCase()}</span>
                    <span className="ml-2 tabular-nums font-medium text-slate-900">{l.clicks}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function Today() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/today").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center gap-2 py-16 text-slate-400"><Loader2 className="animate-spin" /> Caricamento Centro Operativo…</div>;

  const name = (data?.user?.name || user?.name || "").split(" ")[0] || "";
  const counters = data?.counters || [];
  const insights = data?.insights || [];
  const actionable = data?.actionable_count ?? 0;

  return (
    <div className="space-y-8" data-testid="today-center">
      {/* Headline stile Ivy */}
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#0A1629] text-[#C5A059] shadow-sm">
          <Sun size={24} />
        </div>
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {greeting()}{name ? `, ${name}` : ""}.
          </h1>
          <p className="mt-1 text-base text-slate-500" data-testid="today-headline">
            {actionable > 0
              ? <>Oggi hai <b className="text-slate-800">{actionable} {actionable === 1 ? "attività" : "attività"}</b> che richiedono la tua azione.</>
              : "Tutto sotto controllo: nessuna attività urgente da gestire ora."}
          </p>
        </div>
      </div>

      {/* Contatori azionabili */}
      {counters.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {counters.map((c) => {
            const col = COLOR[c.color] || COLOR.blue;
            const Icon = ICONS[c.key] || CheckCircle2;
            const dim = c.count === 0;
            return (
              <button key={c.key} data-testid={`today-counter-${c.key}`} onClick={() => nav(c.link)}
                className={`group flex items-center justify-between rounded-2xl border border-[#EAE7DF] bg-white p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${dim ? "opacity-60" : ""}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: col.dot }} />
                    <span className="text-3xl font-light tabular-nums text-slate-900">{c.count}</span>
                  </div>
                  <div className="mt-1 text-sm text-slate-500">{c.label}</div>
                </div>
                <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${col.bg} ${col.text}`}>
                  <Icon size={18} strokeWidth={1.8} />
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Portali ospite da completare */}
      <GuestPortalsCard />

      {/* Ivy mi consiglia */}
      <Card className="overflow-hidden border-[#EAE7DF]" data-testid="today-ivy-panel">
        <div className="flex items-center gap-2 border-b border-[#EAE7DF] bg-[#F9F8F4] px-5 py-3">
          <Sparkles size={17} className="text-[#C5A059]" />
          <span className="font-head text-sm font-semibold text-slate-800">Ivy mi consiglia</span>
        </div>
        <div className="divide-y divide-[#EAE7DF]">
          {insights.length === 0 && (
            <div className="px-5 py-6 text-sm text-slate-400">Nessun suggerimento per oggi. Ottimo lavoro! 🎯</div>
          )}
          {insights.map((i, idx) => {
            const col = COLOR[i.color] || COLOR.blue;
            return (
              <button key={idx} data-testid={`today-insight-${idx}`} onClick={() => i.link && nav(i.link)}
                className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-[#F9F8F4]">
                <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: col.dot }} />
                <span className="flex-1 text-sm text-slate-700">{i.text}</span>
                <ChevronRight size={16} className="text-slate-300" />
              </button>
            );
          })}
        </div>
      </Card>

      <RebookingCard />
    </div>
  );
}
